import React, { Fragment } from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";

import Media from "../../common/media";

import FormHeader from "../../common/form/formHeader";
import FormFooter from "../../common/form/formFooter";

import {
  getEntities,
  getEntity,
  getMaxPriority,
  saveEntity,
} from "../../../services/apiService";
import { uploadFiles, deleteFile } from "../../../services/fileService";

class PoiFrom extends Media {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleMapClick = this.handleMapClick.bind(this);

    this.state = {
      data: {
        category_id: "",
        priority: 1,
        title_en: "",
        title_de: "",
        description_en: "",
        description_de: "",
        caption_en: "",
        caption_de: "",
        add_info_url: "",
        date: undefined,
        is_displayed_on_table: false,
        has_histocache_location: false,
        lat: "",
        long: "",
        filename: "",
        image_aspect_ratio: "",
        tags: [],
      },
      maxPriority: null,
      categories: [],
      tags: [],
      images: [],
      previewImages: [],
      selectedImages: [],
      labels: [],
      progresses: [],
      currentFiles: { filename: "" },
      selectedFiles: { filename: "" },
      inputLabels: { filename: "" },
      types: { filename: "" },
      errors: {},
    };
  }

  // Define the Form Schema
  schema = {
    _id: Joi.string(),
    category_id: Joi.string().required().label("Category"),
    priority: Joi.number().integer().min(1).label("Priority"),
    title_en: Joi.string().min(3).max(256).required().label("Title"),
    title_de: Joi.string().min(3).max(256).required().label("Titel"),
    description_en: Joi.string()
      .min(5)
      .max(2147483648)
      .required()
      .label("Description"),
    description_de: Joi.string()
      .min(5)
      .max(2147483648)
      .required()
      .label("Beschreibung"),
    caption_en: Joi.string().min(3).max(65536).required().label("Caption"),
    caption_de: Joi.string()
      .min(3)
      .max(65536)
      .required()
      .label("Bildunterschrift"),
    add_info_url: Joi.string()
      .optional()
      .allow(null)
      .allow("")
      .empty("")
      .label("Add Info URL"),
    date: Joi.date().optional().allow(null).allow("").empty("").label("Date"),

    is_displayed_on_table: Joi.boolean().label("Displayed on Table").required(),

    has_histocache_location: Joi.boolean()
      .label("Histocache Location")
      .required(),
    lat: Joi.number()
      .min(-90)
      .max(90)
      .label("Latitude")
      .when("has_histocache_location", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    long: Joi.number()
      .min(-180)
      .max(180)
      .label("Longitude")
      .when("has_histocache_location", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    filename: Joi.string().label("Image").required(),
    image_aspect_ratio: Joi.number()
      .min(0.1)
      .max(4)
      .label("Aspect Ratio")
      .when("has_histocache_location", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    tags: Joi.array().label("Tags").required().default([]),
  };

  // Retrive the Data from the Server
  async componentDidMount() {
    // Get Categories and Modify
    const { data: allCategories } = await getEntities("category");
    if (allCategories.length === 0) return;

    // When at least a Poi exists in the database:
    const categories = allCategories.map(({ name_en: name, ...rest }) => ({
      name,
      ...rest,
    }));

    // Get Tags and Modify
    const { data: allTags } = await getEntities("tag");

    // When at least a Tag exists in the database:
    const tags = allTags.map(({ title_en: name, ...rest }) => ({
      name,
      ...rest,
    }));

    if (allTags.length === 0) {
      this.setState({ categories });
    } else {
      this.setState({ categories, tags });
    }

    // Get the maximum Priority
    const { data: max } = await getMaxPriority("poi");

    // Get the Given ID
    const poi_id = this.props.match.params.id;

    if (poi_id === "new") {
      const data = { ...this.state.data };

      if (max.length === 0) {
        data.priority = 1;
        return this.setState({ data, maxPriority: 1 });
      } else {
        data.priority = max[0].priority + 1;
        return this.setState({ data, maxPriority: max[0].priority });
      }
    }

    // Get Poi related to the Given ID
    const { data: poi } = await getEntity("poi", poi_id);
    if (!poi) return this.props.history.replace("/not-found");

    this.setState({
      data: this.mapToViewModel(poi),
      maxPriority: max[0].priority,
      currentFiles: { filename: poi.filename },
    });
  }

  // Map current Poi to the Form Inputs
  mapToViewModel(poi) {
    return {
      _id: poi._id,
      priority: poi.priority,
      category_id: poi.category_id,
      title_en: poi.title_en,
      title_de: poi.title_de,
      description_en: poi.description_en,
      description_de: poi.description_de,
      caption_en: poi.caption_en,
      caption_de: poi.caption_de,
      add_info_url: poi.add_info_url,
      date: poi.date,
      is_displayed_on_table: poi.is_displayed_on_table,
      has_histocache_location: poi.has_histocache_location,
      lat: poi.lat,
      long: poi.long,
      filename: poi.filename,
      image_aspect_ratio: poi.image_aspect_ratio,
      tags: poi.tags,
    };
  }

  // Form submit Function
  doSubmit = async () => {
    // Data for saving to the Database
    const { data, selectedImages, currentFiles } = this.state;

    try {
      // Saving Poi
      const response = await saveEntity("poi", this.cleanDataObject(data));
      if (response.data && response.status !== 200) return;

      // Uploading File
      if (selectedImages.length) {
        const header = `pois/${response.data._id}`;
        const progressInfos = [...this.state.progresses];

        await uploadFiles(selectedImages[0], header, (event) => {
          progressInfos[0].percentage = Math.round(
            (100 * event.loaded) / event.total
          );

          this.setState({ progresses: progressInfos });
        })
          .then(() => {
            // Success Message of File uploading
            toast.success(
              `File: ${progressInfos[0].fileName} \n uploaded sucessfully!`
            );
          })
          .catch(() => {
            progressInfos[0].percentage = 0;

            this.setState({ progresses: progressInfos });

            // Error Message of File uploading
            toast.error(
              `File: ${progressInfos[0].fileName} \n can not be uploaded.`
            );
          });
      }

      // Success Message of Poi Saving
      toast.success(
        <div>
          Histocache: "{response.data.title_en}" <br /> saved successfully.
        </div>
      );

      // Delete Previous File if exists.
      if (currentFiles.filename && data.filename !== currentFiles.filename)
        await deleteFile("pois", currentFiles.filename);
    } catch (ex) {
      toast.error(ex.response?.message ?? ex.response.data?.message);

      return;
    }

    // Return to Pois TableView after saving
    this.handleBack();
  };

  // Data Object cleaning Function
  cleanDataObject(obj) {
    for (var property in obj) {
      if (
        obj[property] === null ||
        obj[property] === undefined ||
        obj[property] === ""
      ) {
        delete obj[property];
      }
    }
    return obj;
  }

  // Back button Event Handler
  handleBack = () => {
    // Table Pagination Query
    const searchParams = new URLSearchParams(window.location.search);
    const currentPageQuery = searchParams.get("currentPage");
    const pageSizeQuery = searchParams.get("pageSize");

    // Return to Pois TableView after saving
    if (currentPageQuery && pageSizeQuery) {
      this.props.history.push(
        `/dashboard/pois?pageSize=${pageSizeQuery}&currentPage=${currentPageQuery}`
      );
    } else {
      this.props.history.push("/dashboard/pois");
    }
  };

  render() {
    const id = this.props.match.params.id;
    const { categories, tags, data, progresses, errors } = this.state;

    return (
      <div className="col alt-table-responsive">
        <FormHeader name="Histocache" icon="bx bxs-map" id={id} />
        <form
          onSubmit={this.handleSubmit}
          className="form-group"
          encType="multipart/form-data"
        >
          <div className="row pt-2 mt-2">
            <div className=" col-sm-6">
              <div className="row align-items-center">
                <div className="col-sm-8">
                  {this.renderSelect("category_id", "Category", categories)}
                </div>
                <div className="col-sm-4">
                  {this.renderPriority("priority", "Priority")}
                </div>
              </div>
              <div className="row">
                <div className="col-sm-8">
                  {this.renderInput("add_info_url", "Additional info URL")}
                </div>
                <div className="col-sm-4">{this.renderDateInput("date")}</div>
              </div>
              <div className="row mb-4 pb-2">
                <div className="col">
                  {this.renderMultiList(tags, "tags", "Tags", "Select Tags...")}
                </div>
              </div>

              <div className="row mb-4">
                <div className="col">
                  {this.renderToggle(
                    "has_histocache_location",
                    "Histocache Location"
                  )}
                </div>
              </div>
              <div className="row mb-4 mt-2">
                <div className="col">
                  {this.renderToggle(
                    "is_displayed_on_table",
                    "Displayed on Table"
                  )}
                </div>
              </div>

              <hr />

              <div className="row mt-4">
                <div className="col">
                  {this.renderInput("title_en", "Title")}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {this.renderTextBox("description_en", "Description")}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {this.renderTextBox("caption_en", "Caption")}
                </div>
              </div>

              <hr />

              <div className="row mt-4">
                <div className="col">
                  {this.renderInput("title_de", "Titel")}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {this.renderTextBox("description_de", "Beschreibung")}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {this.renderTextBox("caption_de", "Bildunterschrift")}
                </div>
              </div>
            </div>

            <div className=" col-sm-6 border-left">
              {data.has_histocache_location && (
                <Fragment>
                  <div className="row mt-1">
                    <div className="col">
                      {this.renderMap("Histocache Location", "lat", "long")}
                    </div>
                  </div>
                  <div className="row pt-4">
                    <div className="col-sm-6">
                      {this.renderInput("lat", "Latitude", "number")}
                    </div>
                    <div className="col-sm-6">
                      {this.renderInput("long", "Longitude", "number")}
                    </div>
                  </div>
                </Fragment>
              )}

              {data.has_histocache_location && <hr className="mt-3 pb-2" />}

              <div className="row">
                <div className="col">
                  {this.renderMediaUploader(
                    "Image",
                    "filename",
                    "pois",
                    "_id",
                    "image/*",
                    "image_aspect_ratio"
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col">{this.renderErrors(errors)}</div>
              </div>
              <div className="row">
                <div className="col">{this.renderUploadStatus(progresses)}</div>
              </div>
            </div>
          </div>
          <FormFooter
            onBackClick={this.handleBack}
            onValidate={this.validate()}
          />
        </form>
      </div>
    );
  }
}

export default PoiFrom;
